/**
 * Tenant API Service
 *
 * API methods for tenant operations including registration, verification, and updates.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiActionResponse } from '../types/api.types';
import type {
  TenantSanitized,
  TenantCreateDTO,
  TenantUpdateDTO,
  TenantVerificationWithUserRequest,
} from '../types/tenant.types';
import type { UserSanitized } from '../types/user.types';

// ============================================================================
// VERIFICATION RESPONSE TYPE
// ============================================================================

interface TenantVerificationData {
  tenant: TenantSanitized;
  user: UserSanitized;
}

// ============================================================================
// TENANT SERVICE
// ============================================================================

export const tenantService = {
  /**
   * Register a new tenant
   * Returns the tenant with registration message
   */
  registerTenant: async (data: TenantCreateDTO): Promise<{ success: boolean; data: TenantSanitized; message: string }> => {
    const response = await apiClient.post<ApiSuccessResponse<TenantSanitized>>('/tenants', data);
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message || 'Tenant registered successfully',
    };
  },

  /**
   * Verify tenant email with verification token and create admin user
   */
  verifyTenant: async (
    data: TenantVerificationWithUserRequest
  ): Promise<{
    message: string;
    data: TenantVerificationData;
  }> => {
    const response = await apiClient.post<ApiSuccessResponse<TenantVerificationData>>('/tenants/verify', data);
    return { message: response.data.message || 'Verification successful', data: response.data.data };
  },

  /**
   * Resend verification email
   */
  resendVerification: async (tenantCode: string): Promise<{ message: string }> => {
    const response = await apiClient.post<ApiActionResponse>(`/tenants/${tenantCode}/resend-verification`);
    return { message: response.data.message };
  },

  /**
   * Get tenant by ID
   */
  getTenant: async (tenantId: string): Promise<TenantSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<TenantSanitized>>(`/tenants/${tenantId}`);
    return response.data.data;
  },

  /**
   * Update tenant information
   */
  updateTenant: async (tenantId: string, data: TenantUpdateDTO): Promise<TenantSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<TenantSanitized>>(`/tenants/${tenantId}`, data);
    return response.data.data;
  },

  /**
   * Check if tenant code is available
   */
  checkTenantCodeAvailability: async (tenantCode: string): Promise<{ available: boolean }> => {
    const response = await apiClient.get<ApiSuccessResponse<{ available: boolean }>>(`/tenants/check-code/${tenantCode}`);
    return response.data.data;
  },

  /**
   * Upload tenant logo
   * Sends the file directly to the backend for processing
   */
  uploadLogo: async (tenantId: string, file: File): Promise<TenantSanitized> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await apiClient.post<ApiSuccessResponse<TenantSanitized>>(
      `/tenants/${tenantId}/logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Delete tenant logo
   */
  deleteLogo: async (tenantId: string): Promise<TenantSanitized> => {
    const response = await apiClient.delete<ApiSuccessResponse<TenantSanitized>>(
      `/tenants/${tenantId}/logo`
    );
    return response.data.data;
  },
};

export default tenantService;
